import api from './api';
import { slugCardapio } from '../utils/empresa';

export interface Category {
  id: string;
  name: string;
  description: string | null;
  order: number;
  is_active: boolean;
  created_at: string;
}

/** Ingrediente da receita de um prato (insumo ou produto com controle de estoque) */
export interface RecipeItem {
  id: string;
  product_id: string;
  insumo_id: string;
  quantidade: number;
  insumo?: { id: string; name: string; unidade: string; tipo?: string; custo_medio?: string | number };
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_available: boolean;
  exige_cozinha: boolean;
  tipo: string;              // PRODUTO | INSUMO
  exibir_cardapio: boolean;  // aparece no cardápio público e no painel
  controlar_estoque: boolean;
  estoque_atual: number;
  unidade: string;
  consumo_por_venda: number;
  estoque_minimo: number;
  custo_medio: string | number;
  receita?: RecipeItem[];
  category_id: string;
  category?: Category;
  created_at: string;
}

export interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  notes?: string;
  lancado_por?: string;
  cancelado?: boolean;
  lote?: number;
  /** T114: motivo/autor do cancelamento (aparece na comanda) */
  motivo_cancelamento?: string | null;
  cancelado_por?: string | null;
  /** T118: quando o item foi lançado (o "pedir mais" tem data própria) */
  lancado_em?: string;
  product?: Product;
}

export interface Order {
  id: string;
  customer_name: string;
  customer_phone: string | null;
  table_number: number | null;
  status: 'PENDING' | 'CONFIRMED' | 'PREPARING' | 'READY' | 'DELIVERED' | 'PAID' | 'CANCELLED' | 'REQUESTED_BILL';
  total: number;
  notes: string | null;
  items: OrderItem[];
  created_at: string;
  /** T105: quando a comanda foi fechada (paga). null enquanto aberta. */
  paid_at?: string | null;
  /** T106: motivo/autor/data do cancelamento da comanda (reabertura usa isto) */
  motivo_cancelamento?: string | null;
  cancelado_por?: string | null;
  cancelado_em?: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const categoryService = {
  findAll: (params?: { page?: number; limit?: number; busca?: string }) =>
    api.get<PaginatedResponse<Category>>('/categories', { params }),

  findAllActive: () =>
    api.get<{ data: (Category & { products: Product[] })[] }>('/categories/active', {
      params: { restaurante: slugCardapio() },
    }),

  findById: (id: string) =>
    api.get<{ data: Category }>(`/categories/${id}`),

  create: (data: Partial<Category>) =>
    api.post<{ data: Category }>('/categories', data),

  update: (id: string, data: Partial<Category>) =>
    api.put<{ data: Category }>(`/categories/${id}`, data),

  delete: (id: string) =>
    api.delete(`/categories/${id}`),
};

export const productService = {
  findAll: (params?: { page?: number; limit?: number; busca?: string; categoria_id?: string; tipo?: string; controlar_estoque?: boolean }) =>
    api.get<PaginatedResponse<Product>>('/products', { params }),

  findById: (id: string) =>
    api.get<{ data: Product }>(`/products/${id}`),

  create: (data: Partial<Product> & { confirmar_duplicado?: boolean }) =>
    api.post<{ data: Product }>('/products', data),

  update: (id: string, data: Partial<Product> & { confirmar_duplicado?: boolean }) =>
    api.put<{ data: Product }>(`/products/${id}`, data),

  /** Salva a receita do produto: ingredientes consumidos em cada venda */
  setRecipe: (id: string, itens: Array<{ insumo_id: string; quantidade: number }>) =>
    api.put<{ data: RecipeItem[] }>(`/products/${id}/recipe`, { itens }),

  delete: (id: string) =>
    api.delete(`/products/${id}`),
};

export interface Usuario {
  id: string;
  name: string;
  email: string;
  role: string;
  /** Papel DENTRO do restaurante ativo (perfil do vínculo — T103) */
  perfil: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export const usuarioService = {
  findAll: () =>
    api.get<{ data: Usuario[] }>('/users'),

  create: (data: { name: string; email: string; password: string; perfil?: string }) =>
    api.post<{ data: Usuario }>('/users', data),

  update: (id: string, data: { name?: string; email?: string; perfil?: string; is_active?: boolean }) =>
    api.patch<{ data: Usuario }>(`/users/${id}`, data),

  resetPassword: (id: string, password: string) =>
    api.patch(`/users/${id}/password`, { password }),

  /** Troca a própria senha (senha atual + nova) — também serve ao operador global */
  trocarPropriaSenha: (currentPassword: string, newPassword: string) =>
    api.put<{ data: { message: string } }>('/auth/password', { currentPassword, newPassword }),
};

/** Restaurante (tenant) na área do operador da plataforma */
export interface Restaurante {
  id: string;
  name: string;
  slug: string;
  email?: string | null;
  cnpj?: string | null;
  telefone?: string | null;
  logo_url?: string | null;
  is_active: boolean;
  exibir_vitrine?: boolean;
  usuarios: number;
  created_at: string;
}

export const tenantService = {
  listar: () =>
    api.get<{ data: Restaurante[] }>('/tenants'),

  criar: (data: { name: string; slug?: string; email?: string; cnpj?: string; telefone?: string; logo_url?: string }) =>
    api.post<{ data: Restaurante }>('/tenants', data),

  atualizar: (id: string, data: Partial<{ name: string; slug?: string; email?: string; cnpj?: string; telefone?: string; logo_url?: string; is_active: boolean; exibir_vitrine: boolean; motivo: string }>) =>
    api.patch<{ data: Restaurante }>(`/tenants/${id}`, data),

  entrar: (id: string) =>
    api.post<{ data: { token: string; empresa: { id: string; slug: string; nome: string; logo?: string | null; perfil: string; visitante?: boolean } } }>(`/tenants/${id}/entrar`),
};

export const orderService = {
  findAll: (params?: { page?: number; limit?: number; status?: string | string[]; busca?: string; ordenar?: 'asc' | 'desc' }) =>
    api.get<PaginatedResponse<Order>>('/orders', {
      params: params
        ? { ...params, status: Array.isArray(params.status) ? params.status.join(',') : params.status }
        : params,
    }),

  findById: (id: string) =>
    api.get<{ data: Order }>(`/orders/${id}`),

  /** Comanda aberta da mesa (público) — null se a mesa não tem pedido em andamento */
  findOpenByTable: (tableNumber: number) =>
      api.get<{ data: ComandaPublica[] }>('/orders/aberta', { params: { mesa: tableNumber, restaurante: slugCardapio() } }),

    create: (data: {
      customer_name: string;
      customer_phone?: string;
      table_number?: number;
      notes?: string;
      items: Array<{ product_id: string; quantity: number; notes?: string }>;
      merge_into_order_id?: string;
      criar_comanda_nova?: boolean;
      adicionar_itens?: boolean;
      lancado_por?: 'cliente' | 'garcom';
    }) => api.post<{ data: Order }>('/orders', { ...data, lancado_por: data.lancado_por ?? 'cliente' }, { params: { restaurante: slugCardapio() } }),

  updateStatus: (id: string, data: { status: string; notes?: string; motivo?: string }) =>
    api.patch<{ data: Order }>(`/orders/${id}/status`, data),

  /** Cancela UM item da comanda (garçom/admin) — recalculando o total */
  cancelarItem: (orderId: string, itemId: string, motivo?: string) =>
    api.patch<{ data: Order }>(`/orders/${orderId}/items/${itemId}/cancelar`, { motivo }),

  /** Resumo das mesas com comandas abertas (aba Comandas) */
  resumoMesas: () =>
    api.get<{ data: MesaResumo[] }>('/orders/mesas/comandas'),
};

export type PeriodoRelatorio = 'hoje' | 'ontem' | 'semana' | 'mes';

/** T140: registro do log de auditoria (ações que mexem em dinheiro) */
export interface AuditoriaRegistro {
  id: string;
  acao: string;
  entidade: string;
  entidade_id: string;
  detalhe: string | null;
  autor_nome: string | null;
  criado_em: string;
}

export const auditoriaService = {
  listar: (limite = 50) =>
    api.get<{ data: AuditoriaRegistro[] }>('/auditoria', { params: { limite } }),
};

/** Comanda aberta da mesa vista pelo cliente (T134: só o necessário — sem
 * telefone, sem itens detalhados, sem consumo de terceiros) */
export interface ComandaPublica {
  id: string;
  nome: string;
  itens: number;
  total: number;
  status: string;
}

/** Movimentação de estoque (entrada por nota, venda, estorno, baixa manual) */
export interface MovimentacaoEstoque {
  id: string;
  produto_id: string;
  tipo: 'ENTRADA' | 'SAIDA';
  quantidade: number;
  motivo: string | null;
  pedido_id: string | null;
  criado_em: string;
  produto?: { id: string; name: string; tipo: string };
  nota?: { fornecedor: { nome: string } } | null;
}

/** Fornecedor (usado nas notas de entrada) */
export interface Fornecedor {
  id: string;
  nome: string;
  cnpj?: string | null;
  telefone?: string | null;
  observacao?: string | null;
  _count?: { notas: number };
}

/** Item da nota de entrada */
export interface NotaItem {
  id: string;
  produto_id: string;
  quantidade: number;
  preco_unitario: string | number;
  produto?: { id: string; name: string; unidade: string };
}

/** Nota de entrada (documento de compra com vários produtos) */
export interface NotaEntrada {
  id: string;
  numero?: string | null;
  observacao?: string | null;
  created_at: string;
  fornecedor: { id: string; nome: string };
  itens?: NotaItem[];
  _count?: { itens: number };
  /** T128: estorno da nota (devolve estoque e desfaz o custo médio) */
  estornada_em?: string | null;
  estorno_motivo?: string | null;
  estornada_por_nome?: string | null;
}

/** Sugestão de compra: produto no/abaixo do mínimo */
export interface SugestaoCompra {
  id: string;
  name: string;
  unidade: string;
  estoque_atual: number;
  estoque_minimo: number;
  sugestao: number;
  tipo: string;
  custo_medio: string | number;
}

/** Envio de imagem do produto (base64 data URL) — devolve { url: "/uploads/xxx.jpg" } */
export const uploadService = {
  enviarImagem: (base64: string) =>
    api.post<{ data: { url: string } }>('/upload/imagem', { base64 }),
  /** Envio da LOGO do restaurante — pasta própria logo/; tenantId só é
   *  necessário quando o ADMIN da plataforma envia do /admin (sem tenant). */
  enviarLogo: (base64: string, tenantId?: string) =>
    api.post<{ data: { url: string } }>('/upload/logo', { base64, tenantId }),
};

/** Resolve a URL de imagem: relativa (/uploads/...) passa pelo proxy do Vite */
export const resolverImagemUrl = (url: string | null | undefined): string =>
  url || '';

export const estoqueService = {
  /** Lança nota de entrada (vários produtos de uma vez, com preço de custo) */
  criarNota: (data: {
    fornecedor_id: string;
    numero?: string;
    observacao?: string;
    itens: Array<{ produto_id: string; quantidade: number; preco_unitario: number }>;
  }) => api.post<{ data: NotaEntrada }>('/estoque/notas', data),

  /** Lista notas de entrada (mais recentes primeiro) */
  notas: (params?: { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<NotaEntrada>>('/estoque/notas', { params }),

  /** T128: estorna a nota inteira (devolve estoque, desfaz custo médio) */
  estornarNota: (id: string, motivo: string) =>
    api.post<{ data: NotaEntrada }>(`/estoque/notas/${id}/estorno`, { motivo }),

  /** Inventário: corrige o saldo de um produto para o valor contado */
  ajustarInventario: (data: { produto_id: string; saldo_contado: number }) =>
    api.post<{ data: { alterado: boolean; deltaAbs?: number } }>('/estoque/ajuste', data),

  /** T129: inventário em lote — uma transação, resumo explícito */
  inventarioLote: (
    itens: Array<{ produto_id: string; saldo_contado: number; saldo_anterior?: number }>,
  ) =>
    api.post<{
      data: {
        total: number;
        ajustados: number;
        semDiferenca: number;
        falhas: Array<{ produto_id: string; nome: string; motivo: string }>;
        mudouDurante: string[];
      };
    }>('/estoque/inventario', { itens }),

  /** Baixa manual (perda, validade, quebra) */
  baixaManual: (data: { produto_id: string; quantidade: number; motivo: string }) =>
    api.post<{ data: null }>('/estoque/baixa-manual', data),

  /** Lista de compras sugerida (produtos no/abaixo do mínimo) */
  sugestoesCompra: () => api.get<{ data: SugestaoCompra[] }>('/estoque/compras'),

  /** Histórico de movimentações (opcional: filtro por produto) */
  movimentacoes: (params?: { page?: number; limit?: number; produto_id?: string }) =>
    api.get<PaginatedResponse<MovimentacaoEstoque>>('/estoque/movimentacoes', { params }),
};

export const fornecedorService = {
  listar: () => api.get<{ data: Fornecedor[] }>('/fornecedores'),
  criar: (data: { nome: string; cnpj?: string; telefone?: string; observacao?: string }) =>
    api.post<{ data: Fornecedor }>('/fornecedores', data),
  atualizar: (id: string, data: Partial<{ nome: string; cnpj?: string; telefone?: string; observacao?: string }>) =>
    api.put<{ data: Fornecedor }>(`/fornecedores/${id}`, data),
  excluir: (id: string) => api.delete<{ data: null }>(`/fornecedores/${id}`),
};

/** Mesa com suas comandas abertas (aba Comandas) */
export interface MesaResumo {
  mesa: number | null;
  total_comandas: number;
  total_geral: number;
  comandas: Order[];
}

export interface DashboardResumo {
  periodo: {
    inicio: string;
    pedidos: number;
    receita: number;
    ticketMedio: number;
  };
  pendentes: number;
  produtosEsgotados: number;
  /** T127: insumos controlados abaixo do estoque mínimo (não são "esgotados") */
  insumosAbaixoMinimo: number;
  topProdutos: Array<{ nome: string; quantidade: number; receita: number }>;
  topMesas: Array<{ mesa: number; pedidos: number }>;
}

export interface PedidoExportado {
  id: string;
  criadoEm: string;
  cliente: string;
  mesa: number | null;
  status: string;
  total: number;
  itens: Array<{ produto: string; quantidade: number; subtotal: number }>;
}

export const dashboardService = {
  resumo: (periodo?: PeriodoRelatorio) =>
    api.get<{ data: DashboardResumo }>('/dashboard/resumo', { params: { periodo } }),

  exportar: (periodo?: PeriodoRelatorio) =>
    api.get<{ data: PedidoExportado[] }>('/dashboard/exportar', { params: { periodo } }),
};

export interface FinanceiroResumo {
  periodo: { inicio: string; fim: string };
  resumo: {
    receita: number;
    comandasFechadas: number;
    ticketMedio: number;
    itensVendidos: number;
    cancelados: number;
    canceladosQtd: number;
    emAberto: number;
    emAbertoQtd: number;
    pedidosTotal: number;
  };
  porDia: Array<{ dia: string; receita: number; comandas: number }>;
  porCategoria: Array<{ nome: string; quantidade: number; receita: number }>;
  porProduto: Array<{ nome: string; quantidade: number; receita: number }>;
  porMesa: Array<{ mesa: number; receita: number; comandas: number }>;
}

export interface ComandaFechadaExportada {
  data: string;
  mesa: string;
  cliente: string;
  itens: string;
  total: string;
}

export const financeiroService = {
  resumo: (inicio: string, fim: string) =>
    api.get<{ data: FinanceiroResumo }>('/financeiro/resumo', { params: { inicio, fim } }),

  exportar: (inicio: string, fim: string) =>
    api.get<{ data: ComandaFechadaExportada[] }>('/financeiro/exportar', { params: { inicio, fim } }),
};

export const PERIODOS_LABELS: Record<PeriodoRelatorio, string> = {
  hoje: 'Hoje',
  ontem: 'Ontem',
  semana: 'Últimos 7 dias',
  mes: 'Últimos 30 dias',
};
